/**
 * Main App component for BOSI - Body Signal Intelligence.
 *
 * Sets up React Router with authentication-protected routes
 * and provides global AuthContext per research.md.
 */

import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import { AuthProvider } from './contexts/AuthContext';
import Layout from './components/common/Layout';
import PrivateRoute from './components/common/PrivateRoute';
import ErrorBoundary from './components/common/ErrorBoundary';
import AuthForm from './components/auth/AuthForm';
import HomePage from './pages/HomePage';
import CheckInEdit from './pages/CheckInEdit';
import ProgressPage from './pages/ProgressPage';
import ProfilePage from './pages/ProfilePage';
import BPTrackingPage from './pages/BPTrackingPage';
import MeasurementsPage from './pages/MeasurementsPage';
import WelcomePage from './components/welcome/WelcomePage';
import OnboardingPage from './pages/OnboardingPage';
import { BodyInsightRouter } from './pages/body-insight/BodyInsightRouter';

// Create QueryClient instance
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      refetchOnWindowFocus: false,
    },
  },
});

function App() {
  return (
    <ErrorBoundary>
      <QueryClientProvider client={queryClient}>
        <AuthProvider>
          <Router
            future={{
              v7_startTransition: true,
              v7_relativeSplatPath: true,
            }}
          >
            <Routes>
              {/* Public route */}
              <Route path="/auth" element={<AuthForm />} />

              {/* Welcome/Onboarding route (feature 011-registration-welcome) */}
              <Route
                path="/welcome"
                element={
                  <PrivateRoute>
                    <WelcomePage />
                  </PrivateRoute>
                }
              />

              {/* Onboarding routes (feature 016-onboarding-conversation) */}
              <Route
                path="/onboarding/*"
                element={
                  <PrivateRoute>
                    <OnboardingPage />
                  </PrivateRoute>
                }
              />

              {/* Body Insight routes (feature 017-body-insight-summary) */}
              <Route
                path="/body-insight/*"
                element={<BodyInsightRouter />}
              />

              {/* Protected routes */}
              <Route
                path="/"
                element={
                  <PrivateRoute>
                    <Layout>
                      <HomePage />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/checkin/edit"
                element={
                  <PrivateRoute>
                    <Layout>
                      <CheckInEdit />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/progress"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ProgressPage />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/profile"
                element={
                  <PrivateRoute>
                    <Layout>
                      <ProfilePage />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/bp-tracking"
                element={
                  <PrivateRoute>
                    <Layout>
                      <BPTrackingPage />
                    </Layout>
                  </PrivateRoute>
                }
              />
              <Route
                path="/measurements"
                element={
                  <PrivateRoute>
                    <Layout>
                      <MeasurementsPage />
                    </Layout>
                  </PrivateRoute>
                }
              />

              {/* Catch-all redirect */}
              <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </Router>
        </AuthProvider>

        {/* React Query DevTools - development only */}
        {import.meta.env.DEV && <ReactQueryDevtools initialIsOpen={false} />}
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

export default App;
